import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { graphql } from 'gatsby';

import RepairBonusBanner from '../components/home/RepairBonusBanner';
import Step1 from '../components/Step1';
import ProgressBar from '../components/order/ProgressBar';
import { Container, Content, tabletThresholdPixels } from '../components/home/v4/styledComponents';
import Header from '../components/LandingPage/HeaderPartners';
import ArgumentsBanner from '../components/home/v4/ArgumentsBanner';
import ReferrerBanner from '../components/home/ReferrerBanner';
import MenuBar from '../components/home/MenuBar';
import Footer from '../components/home/v3/Footer';
import Layout from '../layouts/index';
import withOrderContext from '../withOrderContext';
import { getInitialStateProp } from '../components/order/preFilledClothes';

export const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1166px;
  margin: 0 auto 64px;
  align-items: center;
  @media (max-width: ${tabletThresholdPixels}) {
    margin: 0 0 40px;
    ${(props) => props.noMarginBottom && 'margin-bottom: 45px;'}
    max-width: 100vw;
    box-sizing: border-box;
  }
`;

class LandingPartnerPageTemplate extends React.Component {
  constructor(props) {
    super(props);
    this.step1Ref = React.createRef();
    this.scrollToStep1 = this.scrollToStep1.bind(this);
  }

  componentDidMount() {
    const { data: { landingsPartnersJson }, orderContext: { checkPromoCode } } = this.props;
    const { landingPage, brand, promoCode } = landingsPartnersJson;
    if (landingPage) {
      localStorage.setItem('landingPage', landingPage);
      localStorage.setItem('landingPage_expiryKey', (new Date()).toISOString());
      if (promoCode) checkPromoCode(promoCode);
    }
    if (brand) { localStorage.setItem('brand', brand); }
    if (typeof window === 'undefined') return;
    window.scrollTo(0, 0);
    window.onbeforeunload = null;
  }

  scrollToStep1() {
    if (this.step1Ref.current) {
      this.step1Ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  renderHeader() {
    const { data: { landingsPartnersJson } } = this.props;
    const {
      fabric, preFilledSteps, landingPage, backgroundImage, mobileBackgroundImage, brandLogo, link, customText, title,
    } = landingsPartnersJson;
    const photoVersion = landingPage || fabric;

    return (
      <Header
        photoVersion={photoVersion}
        curtainsNote={fabric === 'curtains'}
        preFilledSteps={preFilledSteps}
        backgroundImage={backgroundImage?.publicURL}
        mobileBackgroundImage={mobileBackgroundImage?.publicURL}
        brandLogo={brandLogo?.publicURL}
        link={link}
        onScrollToStep1={this.scrollToStep1}
        customText={customText}
        title={title}
      />
    );
  }

  render() {
    const { data: { landingsPartnersJson }, referrer } = this.props;
    if (!landingsPartnersJson) {
      // eslint-disable-next-line react/destructuring-assignment, react/prop-types
      console.warn('No landingsPartnersJson found, props:', this.props?.location);
      return null;
    }
    const {
      slug, preFilledSteps, brand, unavailableCategories, unavailablePieces, unavailableFabrics,
      fabricsOrder, piecesOrder, hasAllCategories, onlyPackageBathFabricForBrand,
    } = landingsPartnersJson;
    const { selectedCategory, selectedFabric } = preFilledSteps || {};
    const location = {
      state: {
        selectedCategory,
        selectedFabric,
      },
    };
    return (
      <Layout routeSlug={slug}>
        <Container>
          <Content full hasNoBanner>
            {referrer && <ReferrerBanner referrer={referrer} />}
            <MenuBar />
            {brand !== 'Lilysilk' && <RepairBonusBanner />}
            {this.renderHeader()}
            <ArgumentsBanner landingPagePath={preFilledSteps?.selectedCategory} />
            <div ref={this.step1Ref} />
            <StepContainer>
              <ProgressBar step={1} />
              <Step1
                initialState={getInitialStateProp(location)}
                isLandingPartnerPage
                landingPageBrand={brand}
                unavailableCategories={unavailableCategories}
                unavailablePieces={unavailablePieces}
                unavailableFabrics={unavailableFabrics}
                fabricsOrder={fabricsOrder}
                piecesOrder={piecesOrder}
                hasAllCategories={hasAllCategories}
                onlyPackageBathFabricForBrand={onlyPackageBathFabricForBrand}
              />
            </StepContainer>
          </Content>
          <Footer />
        </Container>
      </Layout>
    );
  }
}

LandingPartnerPageTemplate.propTypes = {
  data: PropTypes.shape({
    landingsPartnersJson: PropTypes.shape({
      landingPage: PropTypes.string,
      brand: PropTypes.string,
      slug: PropTypes.string,
      fabric: PropTypes.string,
      backgroundImage: PropTypes.shape({
        publicURL: PropTypes.string,
      }),
      mobileBackgroundImage: PropTypes.shape({
        publicURL: PropTypes.string,
      }),
      brandLogo: PropTypes.shape({
        publicURL: PropTypes.string,
      }),
      link: PropTypes.string,
      promoCode: PropTypes.string,
      preFilledSteps: PropTypes.shape({
        selectedCategory: PropTypes.string,
        selectedFabric: PropTypes.string,
      }),
      unavailableCategories: PropTypes.arrayOf(PropTypes.string),
      customText: PropTypes.string,
      unavailablePieces: PropTypes.arrayOf(PropTypes.string),
      unavailableFabrics: PropTypes.arrayOf(PropTypes.string),
      fabricsOrder: PropTypes.arrayOf(PropTypes.string),
      piecesOrder: PropTypes.arrayOf(PropTypes.string),
      hasAllCategories: PropTypes.bool,
      onlyPackageBathFabricForBrand: PropTypes.bool,
      title: PropTypes.string,
    }).isRequired,
  }).isRequired,
  referrer: PropTypes.shape({}),
  orderContext: PropTypes.shape({
    checkPromoCode: PropTypes.func,
  }).isRequired,
};

LandingPartnerPageTemplate.defaultProps = {
  referrer: null,
};
export default withOrderContext(LandingPartnerPageTemplate);

export const landingPartnerPageQuery = graphql`
query landingsPartnersQuery($path: String!) {
  landingsPartnersJson(path: { eq: $path }) {
    slug
    path
    landingPage
    backgroundImage {
      publicURL
    }
    mobileBackgroundImage {
      publicURL
    }
    brandLogo {
      publicURL
    }
    link
    preFilledSteps {
      selectedCategory
      selectedFabric
    }
    unavailableCategories
    customText
    promoCode
    brand
    fabric
    unavailablePieces
    unavailableFabrics
    fabricsOrder
    piecesOrder
    hasAllCategories
    onlyPackageBathFabricForBrand
    title
  }
}`;
